import { Link, type LinkProps } from '@tanstack/react-router'
import { cva, type VariantProps } from 'class-variance-authority'
import type { LucideIcon } from 'lucide-react'

import { cn } from './cn'
import { FOCUS_RING } from './recipes'
import { TONES } from './tone'
import { Tooltip } from './Tooltip'

const badge = cva(
  'inline-flex items-center gap-1 rounded-full font-semibold whitespace-nowrap',
  {
    variants: {
      variant: TONES,
      size: {
        sm: 'px-2 py-0.5 text-[11px]',
        md: 'px-[9px] py-0.5 text-[11.5px]',
      },
    },
    defaultVariants: { variant: 'default', size: 'md' },
  },
)

const GLYPH = { sm: 11, md: 12 } as const

/**
 * A state, as a pill.
 *
 * The five variants are the semantic scale — good, needs attention, broken,
 * neutral, informational — and they are separate from the navy accent so a
 * screen full of brand colour still shows a failure at a glance.
 *
 * `onClick` makes it a control and `link` makes it an anchor: a set of pills is
 * how a screen shows short names that are each a place to go — the categories a
 * section hangs in, the entries that reference this one, an active filter that
 * can be cleared. Prefer `link` where the destination has an address, so the
 * pill keeps a middle click and a back button.
 */
export function Badge({
  label,
  icon: Icon,
  variant,
  size,
  onClick,
  link,
  tooltip,
  className,
}: VariantProps<typeof badge> & {
  label: string
  icon?: LucideIcon
  onClick?: () => void
  /** Where the pill leads, for a destination that has an address. A whole
   *  route descriptor, so the helper that builds one owns which parts it
   *  carries. */
  link?: Pick<LinkProps, 'to' | 'search'>
  /** What the pill names beyond its own word — the group a category sits in. */
  tooltip?: string
  className?: string
}) {
  const content = (
    <>
      {Icon ? <Icon size={GLYPH[size ?? 'md']} aria-hidden /> : null}
      {label}
    </>
  )

  const base = cn(badge({ variant, size }), className)
  const pressable = cn(
    base,
    `transition-colors hover:brightness-95 ${FOCUS_RING}`,
  )

  const pill = link ? (
    <Link {...link} className={pressable}>
      {content}
    </Link>
  ) : onClick ? (
    <button type="button" onClick={onClick} className={pressable}>
      {content}
    </button>
  ) : (
    <span className={base}>{content}</span>
  )

  return tooltip ? <Tooltip label={tooltip}>{pill}</Tooltip> : pill
}

export { badge as badgeRecipe }
